import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

import {
  StatusDropdownContainerDynamic,
  StatusDropdownButtonDynamic,
  StatusDropdownMenuDynamic,
  StatusDropdownItem,
  DropdownOverlay,
  StatusIcon,
} from '../../Css/StyledCalendarMobile.js';

import cancelledIcon from '../../../image/cancelled-icon.png';
import confirmedIcon from '../../../image/confirmed-icon.png';
import pendingIcon from '../../../image/pending-icon.png';

const StatusDropdown = ({
  currentStatus,
  onStatusChange,
  appointmentId,
  onToggle,
  isSmall = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    direction: 'down'
  });

  const dropdownRef = useRef(null);

  const normalizeStatus = (status) => {
    return String(status || 'PENDIENTE').toUpperCase();
  };

  const statusOptions = [
    {
      value: 'PENDIENTE',
      label: 'Pendiente',
      icon: pendingIcon,
      alt: 'Pendiente'
    },
    {
      value: 'REALIZADO',
      label: 'Realizado',
      icon: confirmedIcon,
      alt: 'Realizado'
    },
    {
      value: 'CANCELADO',
      label: 'Cancelado',
      icon: cancelledIcon,
      alt: 'Cancelado'
    },
    {
      value: 'NO_ASISTIO',
      label: 'No asistió',
      icon: cancelledIcon,
      alt: 'No asistió'
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.classList.contains('dropdown-overlay')) {
        setIsOpen(false);

        if (onToggle) {
          onToggle(appointmentId, false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onToggle, appointmentId, isOpen]);

  const handleStatusSelect = (newStatus, event) => {
    event.stopPropagation();

    onStatusChange(appointmentId, newStatus);

    setIsOpen(false);

    if (onToggle) {
      onToggle(appointmentId, false);
    }
  };

  const handleToggleDropdown = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const newIsOpen = !isOpen;

    if (newIsOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const dropdownWidth = 140;

      const baseItemHeight = isSmall ? 32 : 40;
      const verticalPadding = 16;
      const dropdownHeightEstimate =
        statusOptions.length * baseItemHeight + verticalPadding;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldOpenUpwards =
        spaceBelow < dropdownHeightEstimate && spaceAbove > spaceBelow;

      const direction = shouldOpenUpwards ? 'up' : 'down';

      const top = shouldOpenUpwards
        ? Math.max(8, rect.top - dropdownHeightEstimate - 4)
        : Math.max(
            8,
            Math.min(
              rect.bottom + 4,
              window.innerHeight - dropdownHeightEstimate - 8
            )
          );

      let left = rect.right - dropdownWidth;
      left = Math.max(8, Math.min(left, window.innerWidth - dropdownWidth - 8));

      setDropdownPosition({
        top,
        left,
        direction,
      });
    }

    setIsOpen(newIsOpen);

    if (onToggle) {
      onToggle(appointmentId, newIsOpen);
    }
  };

  const getCurrentStatusIcon = () => {
    const normalizedCurrentStatus = normalizeStatus(currentStatus);

    const status = statusOptions.find(
      option => option.value === normalizedCurrentStatus
    );

    return status ? status.icon : pendingIcon;
  };

  const getCurrentStatusAlt = () => {
    const normalizedCurrentStatus = normalizeStatus(currentStatus);

    const status = statusOptions.find(
      option => option.value === normalizedCurrentStatus
    );

    return status ? status.alt : 'Pendiente';
  };

  return (
    <StatusDropdownContainerDynamic
      ref={dropdownRef}
      className={isOpen ? 'dropdown-open' : ''}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
      }}
      isOpen={isOpen}
    >
      <StatusDropdownButtonDynamic
        onClick={handleToggleDropdown}
        aria-label="Cambiar estado del turno"
        aria-expanded={isOpen}
        aria-haspopup="true"
        isOpen={isOpen}
      >
        <StatusIcon
          src={getCurrentStatusIcon()}
          alt={getCurrentStatusAlt()}
          isSmall={isSmall}
        />
      </StatusDropdownButtonDynamic>

      {isOpen && createPortal(
        <>
          <DropdownOverlay className="dropdown-overlay" />

          <StatusDropdownMenuDynamic
            role="menu"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
            }}
            top={dropdownPosition.top}
            left={dropdownPosition.left}
            direction={dropdownPosition.direction}
          >
            {statusOptions.map((option) => (
              <StatusDropdownItem
                key={option.value}
                onClick={(event) => handleStatusSelect(option.value, event)}
                role="menuitem"
                aria-label={`Cambiar a ${option.label}`}
              >
                <StatusIcon
                  src={option.icon}
                  alt={option.alt}
                  isSmall={isSmall}
                />

                {option.label}
              </StatusDropdownItem>
            ))}
          </StatusDropdownMenuDynamic>
        </>,
        document.body
      )}
    </StatusDropdownContainerDynamic>
  );
};

export default StatusDropdown;